import { useEffect, useMemo, useState } from 'react';
import { api } from '../api';
import { useChat } from '../store';
import { ConversationListItem } from './ConversationListItem';
import { NewChatModal } from './NewChatModal';
import { SearchModal } from './SearchModal';
import { IconPlus, IconRefresh, IconSearch } from './Icon';

type Filter = 'all' | 'unread' | 'favorites' | 'groups';

const FILTERS: Array<{ id: Filter; label: string }> = [
  { id: 'all', label: 'All' },
  { id: 'unread', label: 'Unread' },
  { id: 'favorites', label: 'Favorites' },
  { id: 'groups', label: 'Groups' },
];

function countFor(filter: Filter, conversations: { unreadCount: number; isFavorite: boolean; type: string }[]): number {
  switch (filter) {
    case 'all':
      return conversations.length;
    case 'unread':
      return conversations.filter((c) => c.unreadCount > 0).length;
    case 'favorites':
      return conversations.filter((c) => c.isFavorite).length;
    case 'groups':
      return conversations.filter((c) => c.type === 'group' || c.type === 'channel').length;
  }
}

export function Sidebar() {
  const conversations = useChat((s) => s.conversations);
  const conversationsLoaded = useChat((s) => s.conversationsLoaded);
  const setConversations = useChat((s) => s.setConversations);
  const selectedConversationId = useChat((s) => s.selectedConversationId);
  const selectConversation = useChat((s) => s.selectConversation);
  // Active channel filter — when set, only conversations grouped under
  // that channel show in the sidebar. null = all chats (every conv the
  // user is a member of, regardless of channel).
  const activeChannelId = useChat((s) => s.activeChannelId);
  const activeChannel = useChat((s) =>
    s.activeChannelId ? s.channels.find((c) => c.id === s.activeChannelId) : null,
  );
  const [newChatOpen, setNewChatOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  // Collapsible conversation list: persists across reloads. Lets the open
  // thread use the full width (handy in the embedded Connect dock).
  const [collapsed, setCollapsedState] = useState(() => {
    try { return localStorage.getItem('connect_convlist_collapsed') === '1'; } catch { return false; }
  });
  const setCollapsed = (v: boolean) => {
    setCollapsedState(v);
    try { localStorage.setItem('connect_convlist_collapsed', v ? '1' : '0'); } catch { /* ignore */ }
  };
  const [filter, setFilter] = useState<Filter>('all');
  const [dragId, setDragId] = useState<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  async function refresh() {
    setRefreshing(true);
    try {
      const res = await api.conversations();
      setConversations(res.conversations);
    } catch {
      /* ignore */
    } finally {
      setRefreshing(false);
    }
  }

  function reorderFavorites(fromId: string, toId: string) {
    if (fromId === toId) return;
    const favs = conversations.filter((c) => c.isFavorite);
    const fromIdx = favs.findIndex((c) => c.id === fromId);
    const toIdx = favs.findIndex((c) => c.id === toId);
    if (fromIdx === -1 || toIdx === -1) return;
    const next = [...favs];
    const [moved] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, moved!);
    const newIds = next.map((c) => c.id);

    // Optimistically reorder the global list so the UI reflects the drop
    // before the server roundtrip. Non-favorites keep their existing
    // position; we just splice the favorites cluster in the new order at
    // the top of the list.
    const nonFavs = conversations.filter((c) => !c.isFavorite);
    const byId = new Map(conversations.map((c) => [c.id, c]));
    const orderedFavs = newIds.map((id) => byId.get(id)!).filter(Boolean);
    setConversations([...orderedFavs, ...nonFavs]);

    void api.reorderFavorites(newIds).catch(() => {
      // On failure, restore from the server's view by triggering a refetch.
      void api.conversations().then((r) => setConversations(r.conversations));
    });
  }

  const filtered = useMemo(() => {
    return conversations.filter((c) => {
      // The DMs view holds conversations not filed under any channel; a
      // channel holds the chats created in it.
      if (activeChannelId) {
        if (c.channelId !== activeChannelId) return false;
      } else {
        if (c.channelId !== null) return false;
      }
      switch (filter) {
        case 'unread':
          return c.unreadCount > 0;
        case 'favorites':
          return c.isFavorite;
        case 'groups':
          return c.type === 'group' || c.type === 'channel';
        case 'all':
        default:
          return true;
      }
    });
  }, [conversations, filter, activeChannelId]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    const onOpen = () => setSearchOpen(true);
    window.addEventListener('keydown', onKey);
    // Other components (info panel quick-action) ask the sidebar to open the
    // search modal via this custom event rather than prop-drilling state.
    window.addEventListener('chat:open-search', onOpen);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('chat:open-search', onOpen);
    };
  }, []);

  if (collapsed) {
    return (
      <aside className="w-11 shrink-0 hairline flex flex-col items-center pt-5 h-full bg-zinc-950/30">
        <button
          onClick={() => setCollapsed(false)}
          title="Show conversations"
          aria-label="Show conversations"
          className="w-9 h-9 rounded-xl bg-zinc-900/60 hover:bg-zinc-900 hairline text-zinc-400 hover:text-zinc-100 transition-all duration-300 ease-spring active:scale-[0.94] flex items-center justify-center text-lg leading-none"
        >»</button>
      </aside>
    );
  }

  const renderRow = (c: (typeof conversations)[number]) => {
    const draggable = filter === 'favorites' && c.isFavorite;
    return (
      <div
        key={c.id}
        draggable={draggable}
        onDragStart={(e) => {
          if (!draggable) return;
          setDragId(c.id);
          e.dataTransfer.effectAllowed = 'move';
          e.dataTransfer.setData('text/plain', c.id);
        }}
        onDragOver={(e) => {
          if (!draggable || !dragId || dragId === c.id) return;
          e.preventDefault();
          e.dataTransfer.dropEffect = 'move';
          setDragOverId(c.id);
        }}
        onDragLeave={() => {
          if (dragOverId === c.id) setDragOverId(null);
        }}
        onDrop={(e) => {
          if (!draggable || !dragId) return;
          e.preventDefault();
          reorderFavorites(dragId, c.id);
          setDragId(null);
          setDragOverId(null);
        }}
        onDragEnd={() => {
          setDragId(null);
          setDragOverId(null);
        }}
        className={
          dragOverId === c.id
            ? 'rounded-xl ring-2 ring-indigo-400/60 transition'
            : dragId === c.id
              ? 'opacity-40 transition'
              : 'transition'
        }
      >
        <ConversationListItem
          conversation={c}
          selected={c.id === selectedConversationId}
          onClick={() => selectConversation(c.id)}
        />
      </div>
    );
  };

  return (
    <aside className="w-80 hairline flex flex-col h-full bg-zinc-950/30">
      <header className="px-5 pt-5 pb-3 flex items-center justify-between">
        <div>
          <p className="text-[10px] font-medium uppercase tracking-[0.18em] text-zinc-500 mb-0.5">
            {activeChannel ? 'Channel' : 'Direct'}
          </p>
          <h1 className="text-lg font-semibold tracking-tight truncate flex items-center gap-1.5">
            {activeChannel ? (
              <>
                {activeChannel.iconEmoji && (
                  <span className="text-base">{activeChannel.iconEmoji}</span>
                )}
                <span className="truncate">{activeChannel.name}</span>
              </>
            ) : (
              'Messages'
            )}
          </h1>
        </div>
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => setCollapsed(true)}
            title="Hide conversations"
            aria-label="Hide conversations"
            className="w-9 h-9 rounded-xl bg-zinc-900/60 hover:bg-zinc-900 hairline text-zinc-400 hover:text-zinc-100 transition-all duration-300 ease-spring active:scale-[0.94] flex items-center justify-center text-lg leading-none"
          >«</button>
          <button
            onClick={() => void refresh()}
            disabled={refreshing}
            title="Refresh chats"
            aria-label="Refresh chats"
            className="group w-9 h-9 rounded-xl bg-zinc-900/60 hover:bg-zinc-900 hairline text-zinc-400 hover:text-zinc-100 disabled:opacity-50 transition-all duration-300 ease-spring active:scale-[0.94] flex items-center justify-center"
          >
            <IconRefresh
              size={13}
              className={`transition-transform duration-700 ease-spring ${
                refreshing ? 'animate-spin' : 'group-hover:rotate-90'
              }`}
            />
          </button>
          <button
            onClick={() => setNewChatOpen(true)}
            title="New chat"
            aria-label="New chat"
            className="group relative w-9 h-9 rounded-xl bg-zinc-900 hover:bg-zinc-800 surface-raised text-zinc-300 hover:text-zinc-50 transition-all duration-300 ease-spring active:scale-[0.94] flex items-center justify-center"
          >
            <IconPlus size={14} className="transition-transform duration-300 ease-spring group-hover:rotate-90" />
          </button>
        </div>
      </header>

      <div className="px-3 pt-1">
        <button
          onClick={() => setSearchOpen(true)}
          className="w-full rounded-xl bg-zinc-900/60 hover:bg-zinc-900 hairline px-3.5 py-2 text-xs text-zinc-400 hover:text-zinc-200 transition-all duration-300 ease-spring flex items-center gap-2.5 surface-deep"
        >
          <IconSearch size={13} className="text-zinc-500" />
          <span className="flex-1 text-left">Search messages…</span>
          <kbd className="text-[10px] font-mono text-zinc-500 bg-zinc-950/80 hairline px-1.5 py-0.5 rounded-md">
            ⌘K
          </kbd>
        </button>
      </div>

      <div className="px-3 py-3 flex gap-1.5 overflow-x-auto">
        {FILTERS.map((f) => {
          const active = filter === f.id;
          const count = countFor(f.id, conversations);
          const showCount = count > 0 && (f.id !== 'all' || active);
          return (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              className={`shrink-0 px-3 py-1 text-[11px] font-medium rounded-full transition-all duration-300 ease-spring inline-flex items-center gap-1.5 ${
                active
                  ? 'bg-zinc-50 text-zinc-950 shadow-inset-hairline-strong'
                  : 'text-zinc-400 hover:text-zinc-200 hover:bg-zinc-900/60'
              }`}
            >
              <span>{f.label}</span>
              {showCount && (
                <span
                  className={`tabular-nums text-[10px] rounded-full px-1.5 min-w-[16px] text-center ${
                    active
                      ? 'bg-zinc-900 text-zinc-300'
                      : 'bg-zinc-900/80 text-zinc-500'
                  }`}
                >
                  {count > 99 ? '99+' : count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      <div className="flex-1 overflow-y-auto px-2 py-1 space-y-0.5">
        {!conversationsLoaded ? (
          // Initial-load skeleton — renders six placeholder rows with a
          // shimmer so the sidebar doesn't flash blank or flash the
          // "no conversations" empty state on a slow first fetch.
          <ul className="space-y-1 px-1 py-1">
            {Array.from({ length: 6 }).map((_, i) => (
              <li
                key={i}
                className="flex items-center gap-3 rounded-xl px-2 py-2.5"
              >
                <div className="size-10 rounded-full bg-zinc-900 animate-pulse" />
                <div className="flex-1 space-y-1.5">
                  <div className="h-3 w-3/4 bg-zinc-900 rounded animate-pulse" />
                  <div className="h-2.5 w-1/2 bg-zinc-900 rounded animate-pulse" />
                </div>
              </li>
            ))}
          </ul>
        ) : conversations.length === 0 ? (
          <p className="text-center text-xs text-zinc-500 py-10 px-6 leading-relaxed">
            No conversations yet. Start one with the
            <span className="inline-block mx-1 text-zinc-300">+</span>
            button above.
          </p>
        ) : filtered.length === 0 ? (
          <p className="text-center text-xs text-zinc-500 py-10 px-6">
            Nothing matches that filter.
          </p>
        ) : (
          filtered.map(renderRow)
        )}
      </div>

      {newChatOpen && <NewChatModal onClose={() => setNewChatOpen(false)} />}
      {searchOpen && <SearchModal onClose={() => setSearchOpen(false)} />}
    </aside>
  );
}
